/**
 * The satellite-and-streets image that sits behind the town map.
 *
 * Google's Static Maps API, not the Maps JS SDK: one image request per render
 * rather than a billed interactive map load, no SDK on a page that otherwise
 * carries no third-party JavaScript, and the existing SVG overlay - markers,
 * shapes, tooltips, filter chips - survives untouched on top of it.
 *
 * `hybrid` is satellite imagery with the road and label layer drawn over it.
 * Plain `satellite` has no streets, which is the half of the question that
 * makes a parcel findable.
 *
 * No key configured means no backdrop: `basemapUrl` returns null and the map
 * renders on its plain surface exactly as before. The key is read from the
 * page rather than compiled in, so it is set per deployment.
 */

const ENDPOINT = 'https://maps.googleapis.com/maps/api/staticmap';

/** Static Maps caps a single request at 640x640 before the scale multiplier. */
export const MAX_REQUEST_PX = 640;

/**
 * `scale=2` returns twice the pixels for the same ground area and the same
 * quota cost, which is what keeps the image sharp when the card is wider than
 * the 640px the API will serve.
 */
const SCALE = 2;

/**
 * Builds the image URL for a view produced by `fitView`.
 *
 * Returns null when there is no key or no view, which the renderer treats as
 * "draw the plain surface" rather than as an error - the dashboard must not
 * depend on a third party being reachable to show a town's listings.
 */
export function basemapUrl(view, apiKey, { mapType = 'hybrid' } = {}) {
  if (!view || !apiKey) return null;

  const params = new URLSearchParams({
    center: `${view.centerLat.toFixed(6)},${view.centerLng.toFixed(6)}`,
    zoom: String(view.zoom),
    size: `${view.width}x${view.height}`,
    scale: String(SCALE),
    maptype: mapType,
    // Imagery compresses far better as JPEG, and the overlay carries the
    // crisp edges that would suffer from it.
    format: 'jpg',
    key: apiKey
  });

  return `${ENDPOINT}?${params.toString()}`;
}

/**
 * The attribution Google's terms require whenever Static Maps imagery is
 * shown outside the Maps JS SDK, which draws its own.
 */
export const BASEMAP_ATTRIBUTION = 'Imagery © Google';
